<template>
    <div :class="props.class">
        <LoadingDialog v-if="savingCategory" />
        <h1 class="text-2xl font-bold mb-4">
            {{ categoryData.id ? 'Editar Categoria' : 'Formulário de Categoria' }}
        </h1>
        
        <form @submit.prevent="saveCategory">
            <div class="mb-4">
                <label for="parentCategory" class="block text-sm font-medium text-gray-700">
                    Categorias Vinculadas / Superiores <RequiredMarker :isValid="dataToValidate.parentCategory" />
                </label>
                <MultiSelect 
                    id="parentCategory"
                    v-model="categoryData.parentCategory"
                    :loading="categoriesSelectData.loading"
                    :options="categoriesSelectData.data"
                    optionLabel="name"
                    :filter="true"
                    dataKey="id" 
                    placeholder="Selecione as categorias vinculadas"
                    display="chip"
                    class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                    <template #chip="slotProps">
                        <div class="flex items-center gap-1.5 px-2 py-0.5 text-xs bg-gray-100 text-gray-800 rounded">
                            <span>{{ slotProps.value.pt || slotProps.value.name }}</span>
                        </div>
                    </template>

                    <template #option="slotProps">
                        <div class="flex flex-col">
                            <span class="font-medium text-sm">{{ slotProps.option.pt || slotProps.option.name }}</span>
                            <span class="text-xs text-gray-400 font-mono">{{ slotProps.option.name }}</span>
                        </div>
                    </template>
                </MultiSelect>
            </div>

            <div class="w-full text-center mt-10 border-t border-iron pt-10">
                <Button 
                    type="submit" 
                    class="px-4 py-2 bg-blue-600 text-white rounded-md no-bg-button" 
                >
                    Enviar
                </Button>
            </div>
        </form>
    </div>
</template>

<script setup>
    import { defineProps, defineEmits, onMounted, ref, watch, reactive } from 'vue';
    import MultiSelect from 'primevue/multiselect';
    import Button from 'primevue/button';
    import { useToast } from 'primevue/usetoast';
    import { showToast } from '@/composables/toast';
    import utils from '@/composables/utils';
    import { translate } from '@/composables/translate';
    import LoadingDialog from '@/components/Utils/LoadingDialog.vue';
    import RequiredMarker from '@/components/misc/RequiredMarkerComponent.vue';
    
    import CategoryService from '@/services/CategoryService'; 
    import CourseService from '@/services/CourseService';

    const emit = defineEmits(["actionPerformed"]);
    const toast = useToast();
    
    const props = defineProps({
        categoryData: {
            type: Object,
            required: true
        },
        class: {
            type: String,
            required: false,
            default: ''
        },
        courseUuid: {
            type: String,
            required: true
        }
    });

    let savingCategory = ref(false);
    let courseUuid = ref(null);
    
    let categoryData = ref({
        id: null,
        parentCategory: []
    });

    let categoriesSelectData = ref({
        data: [],
        loading: true
    });

    let rawParentCategories = [];

    // Ajuste estratégico: O ciclo de vida garante a ordem de execução
    onMounted(async () => {
        courseUuid.value = props.courseUuid;
        setValues(props.categoryData);
        await loadCategoriesList();
    });

    function emitSuccess() {
        emit("actionPerformed", true);
    }

    watch(() => props.categoryData, (newValue) => {
        setValues(newValue);
        loadMyCategories();
    },
        { deep: true }
    );

    watch(() => props.courseUuid, (newValue) => {
        courseUuid.value = newValue;
    },
        { deep: true }
    );

    const dataToValidate = reactive({
        parentCategory: true
    });

    async function loadCategoriesList() {
        categoriesSelectData.value.loading = true;
        const response = await CategoryService.all();
        
        let content = [];
        if (response && typeof response.success === 'function' && response.success()) {
            content = response.getContent() || [];
        } else if (Array.isArray(response)) {
            content = response;
        }

        if (categoryData.value.id) {
            categoriesSelectData.value.data = content.filter(cat => String(cat.id) !== String(categoryData.value.id));
        } else {
            categoriesSelectData.value.data = content;
        }

        await loadMyCategories();
        categoriesSelectData.value.loading = false;
    }

    async function loadMyCategories() {
        categoryData.value.parentCategory = await CategoryService.getByCourse(courseUuid.value);
    }

    function setValues(values = {}) {
        if (!values) return;
        
        categoryData.value.id = values.id !== undefined ? values.id : null;
        
        if (values.parentCategory !== undefined && values.parentCategory !== null) {
            rawParentCategories = Array.isArray(values.parentCategory) 
                ? values.parentCategory 
                : [values.parentCategory];
        } else {
            rawParentCategories = [];
        }

        if (categoriesSelectData.value.data.length > 0) {
            setParentCategory();
        }
    }

    function setParentCategory() {
        if (!rawParentCategories || rawParentCategories.length === 0) {
            categoryData.value.parentCategory = [];
            return;
        }
        
        const targetIds = rawParentCategories.map(item => 
            String(typeof item === 'object' && item !== null ? item.id : item)
        );

        let linkedObjects = [];
        for (let cat of categoriesSelectData.value.data) {
            if (targetIds.includes(String(cat.id))) {
                linkedObjects.push(cat);
            }
        }
        
        categoryData.value.parentCategory = linkedObjects;
    }

    async function saveCategory() {
        let dataToSave = utils.copyVariable(categoryData.value);
        
        if (Array.isArray(dataToSave.parentCategory)) {
            dataToSave.parentCategory = dataToSave.parentCategory.map(cat => 
                typeof cat === 'object' && cat !== null ? cat.id : cat
            );
        }

        savingCategory.value = true;
        const response = await CourseService.saveCategory(courseUuid.value, dataToSave.parentCategory);
        savingCategory.value = false;

        if (!response.success()) {
            showToast(toast, response.getMessage(), 'error');
            return;
        }

        showToast(toast, translate('categories saved', 'ucfirst'));
        emitSuccess();
    }
</script>